/**
 * SurfaceInsetRing — radial brush-cursor handle for surface inset drags.
 *
 * Mirrors the visual of the edit-mode `inset_faces` ring in `OperatorGizmo`:
 * a screen-space-sized ring + invisible hit disc on the face plane. The user
 * grabs the disc and drags; the radial component of the pointer projected
 * onto the face plane drives the inset distance.
 *
 * The component is purely presentational + interactive — caller owns:
 *   - `getAnchor` / `getNormal` (world-space anchor + face normal, fetched at
 *     pointerdown so the projection stays stable through camera motion)
 *   - `setInset` (called on every coalesced pointer-move with the new world
 *     distance)
 *   - `confirm` (called on pointerup) and `setDragging` (camera lock)
 */
import { type ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import { forwardRef, useCallback, useEffect, useRef } from "react";
import * as THREE from "three/webgpu";
import { intersectRayWithBasisPlane } from "../../modeling/basisPlaneMath.ts";
import type { FaceBasis } from "../../modeling/faceBasis.ts";
import { createRafCoalescer } from "../../rafCoalescer.ts";
import { screenToWorldRadius } from "../screenRadius.ts";
import {
  DEFAULT_OPACITY,
  GIZMO_SCREEN_PX,
  OP_HANDLE_COLOR,
  OP_HANDLE_COLOR_HOVER,
} from "./constants.ts";
import { applyGizmoRenderOrder } from "./renderOrder.ts";

// Ring + hit disc constants (match `OperatorGizmo.tsx` for visual parity).
const RING_SEGMENTS = 48;
const RING_INNER = 0.92;
const RING_OUTER = 1.0;
const RING_IDLE_RADIUS = 0.6;
const RING_HIT_RADIUS = 0.8;

const ringGeo = new THREE.RingGeometry(RING_INNER, RING_OUTER, RING_SEGMENTS);
const ringHitGeo = new THREE.CircleGeometry(RING_HIT_RADIUS, RING_SEGMENTS);

const invisibleMaterial = new THREE.MeshBasicMaterial({
  visible: false,
  side: THREE.DoubleSide,
});

const _upZ = new THREE.Vector3(0, 0, 1);

interface SurfaceInsetRingProps {
  /** World-space anchor (the cap centroid). */
  computeAnchor: () => readonly [number, number, number] | null;
  /** Face frame for projecting the pointer onto the face plane. */
  computeBasis: () => FaceBasis | null;
  /** Live update — caller stores the new inset distance in the draft. */
  setInset: (distance: number) => void;
  /** Pointer release — caller confirms the inset and advances the stage. */
  confirm: () => void;
  /** Camera lock toggle for the duration of the drag. */
  setDragging: (dragging: boolean) => void;
}

export const SurfaceInsetRing = forwardRef<THREE.Group, SurfaceInsetRingProps>(
  function SurfaceInsetRing(
    { computeAnchor, computeBasis, setInset, confirm, setDragging },
    forwardedRef,
  ) {
    const { camera, size: viewportSize } = useThree();
    const localGroupRef = useRef<THREE.Group>(null);
    const ringRef = useRef<THREE.Mesh>(null);
    const hoveredRef = useRef(false);
    const draggingRef = useRef(false);
    const cleanupRef = useRef<(() => void) | null>(null);

    // The pointerdown anchor + basis are captured so the projection stays
    // stable if the camera moves mid-drag.
    const dragAnchor = useRef<[number, number, number] | null>(null);
    const dragBasis = useRef<FaceBasis | null>(null);
    const dragStartUv = useRef<[number, number] | null>(null);

    const ringMaterial = useRef(
      new THREE.MeshBasicMaterial({
        color: OP_HANDLE_COLOR,
        transparent: true,
        opacity: DEFAULT_OPACITY,
        depthTest: false,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    const ringHoverMaterial = useRef(
      new THREE.MeshBasicMaterial({
        color: OP_HANDLE_COLOR_HOVER,
        transparent: true,
        opacity: 1.0,
        depthTest: false,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );

    useFrame(() => {
      const group = localGroupRef.current;
      if (!group) return;
      const anchor = computeAnchor();
      const basis = computeBasis();
      if (!anchor || !basis) {
        group.visible = false;
        return;
      }
      group.visible = true;
      group.position.set(anchor[0], anchor[1], anchor[2]);
      const n = new THREE.Vector3(
        basis.normal[0],
        basis.normal[1],
        basis.normal[2],
      );
      group.quaternion.setFromUnitVectors(_upZ, n);
      group.scale.setScalar(
        screenToWorldRadius(
          GIZMO_SCREEN_PX,
          group.position,
          camera,
          viewportSize.height,
        ),
      );
      if (ringRef.current) {
        ringRef.current.material = hoveredRef.current
          ? ringHoverMaterial.current
          : ringMaterial.current;
        ringRef.current.scale.setScalar(RING_IDLE_RADIUS);
      }
    });

    useEffect(() => {
      const g = localGroupRef.current;
      if (g) applyGizmoRenderOrder(g, 999);
    });

    // Forward the local group ref to the parent.
    useEffect(() => {
      if (!forwardedRef) return;
      if (typeof forwardedRef === "function") {
        forwardedRef(localGroupRef.current);
      } else {
        forwardedRef.current = localGroupRef.current;
      }
    });

    const projectClientToBasisUv = useCallback(
      (
        clientX: number,
        clientY: number,
        anchor: readonly [number, number, number],
        basis: FaceBasis,
      ): [number, number] | null => {
        const canvas = document.querySelector("canvas") as HTMLCanvasElement | null;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        const ndcX = ((clientX - rect.left) / rect.width) * 2 - 1;
        const ndcY = -((clientY - rect.top) / rect.height) * 2 + 1;
        const out = new THREE.Vector3();
        const o = new THREE.Vector3();
        const d = new THREE.Vector3();
        const hit = intersectRayWithBasisPlane(
          ndcX,
          ndcY,
          camera,
          basis,
          out,
          o,
          d,
        );
        if (!hit) return null;
        const dx = hit.x - anchor[0];
        const dy = hit.y - anchor[1];
        const dz = hit.z - anchor[2];
        const u =
          dx * basis.tangent[0] +
          dy * basis.tangent[1] +
          dz * basis.tangent[2];
        const v =
          dx * basis.bitangent[0] +
          dy * basis.bitangent[1] +
          dz * basis.bitangent[2];
        return [u, v];
      },
      [camera],
    );

    const handlePointerDown = useCallback(
      (e: ThreeEvent<PointerEvent>) => {
        if (draggingRef.current) return;
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();

        const anchor = computeAnchor();
        const basis = computeBasis();
        if (!anchor || !basis) return;

        const startUv = projectClientToBasisUv(
          e.nativeEvent.clientX,
          e.nativeEvent.clientY,
          anchor,
          basis,
        );
        if (!startUv) return;

        dragAnchor.current = [anchor[0], anchor[1], anchor[2]];
        dragBasis.current = basis;
        dragStartUv.current = startUv;
        draggingRef.current = true;
        setDragging(true);

        const livePreview = createRafCoalescer<{ x: number; y: number }>(
          ({ x, y }) => {
            const a = dragAnchor.current;
            const b = dragBasis.current;
            const su = dragStartUv.current;
            if (!a || !b || !su) return;
            const uv = projectClientToBasisUv(x, y, a, b);
            if (!uv) return;
            // Inset increases as the cursor moves toward the centroid.
            // Distance from anchor at start ≈ initial cursor distance from
            // centroid; subtract current distance from that. Clamp to 0.
            const startDist = Math.hypot(su[0], su[1]);
            const curDist = Math.hypot(uv[0], uv[1]);
            const inset = Math.max(0, startDist - curDist);
            setInset(inset);
          },
        );

        const onMove = (ev: PointerEvent) => {
          livePreview.schedule({ x: ev.clientX, y: ev.clientY });
        };
        const cleanup = () => {
          livePreview.cancel();
          window.removeEventListener("pointermove", onMove);
          window.removeEventListener("pointerup", onUp);
          window.removeEventListener("pointercancel", onCancel);
          draggingRef.current = false;
          setDragging(false);
          dragAnchor.current = null;
          dragBasis.current = null;
          dragStartUv.current = null;
          cleanupRef.current = null;
        };
        const onUp = () => {
          livePreview.flush();
          cleanup();
          confirm();
        };
        const onCancel = () => {
          cleanup();
        };
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onCancel);
        cleanupRef.current = cleanup;
      },
      [computeAnchor, computeBasis, projectClientToBasisUv, setInset, confirm, setDragging],
    );

    useEffect(() => {
      return () => {
        if (cleanupRef.current) cleanupRef.current();
      };
    }, []);

    return (
      <group ref={localGroupRef} userData={{ excludeFromExport: true }}>
        <mesh ref={ringRef} geometry={ringGeo} renderOrder={999} />
        {/* Invisible hit disc — larger than visual ring for easier grabbing. */}
        <mesh
          geometry={ringHitGeo}
          material={invisibleMaterial}
          renderOrder={999}
          onPointerDown={handlePointerDown}
          onPointerOver={() => {
            hoveredRef.current = true;
          }}
          onPointerOut={() => {
            hoveredRef.current = false;
          }}
        />
      </group>
    );
  },
);
